import { STATE_ERROR, STATE_LOADED, stateType } from './../state';
import {
  ANSWER_TYPE,
  RR_QUESTION_SOURCE,
  SELECTION_TYPE,
  CONTROL_TYPE,
} from 'src/constants/roleRequirementsTypes';
import { type Instance, types, flow, getRoot, cast } from 'mobx-state-tree';
import type { IStore } from '../Store';
import fetchQuestionnaire from 'src/graphql/hirer/fetchQuestionnaire';
import { logError } from 'src/utils/logError';
import {
  buildSimplifiedAnswers,
  getHardcodedSimplifiedAnswerText,
} from './utils/buildSimplifiedAnswers';
import { isAuOrNzRRQuestion } from './utils/isAuOrNzRRQuestion';
import type { MandatoryQuestions } from 'src/App/CandidateManagementPage/BillableApplicantsDialog';

const simplifiedAnswerIds = types.model({
  id: types.string,
  globalId: types.maybeNull(types.string),
});

export const QuestionnaireAnswer = types
  .model({
    id: types.maybeNull(types.string),
    globalId: types.maybeNull(types.string),
    text: types.string,
    isPreferred: types.boolean,
    order: types.number,
    answerIds: types.maybeNull(types.array(types.string)), // localIds
    answerGlobalIds: types.maybeNull(types.array(types.string)),
  })
  .views((self) => ({
    get globalIdOrId() {
      return self.globalId ?? (self.id as string);
    },
    get answerGlobalIdsOrIds() {
      if (self.answerGlobalIds == null && self.answerIds == null) {
        return null;
      }

      const answerGlobalIdsLength = self.answerGlobalIds?.length ?? 0;
      const answerIdsLength = self.answerIds?.length ?? 0;

      if (answerGlobalIdsLength >= answerIdsLength) {
        return self.answerGlobalIds;
      }
      return self.answerIds;
    },
  }));

export interface IQuestionnaireAnswer
  extends Instance<typeof QuestionnaireAnswer> {}

export const HirerExperience = types.model({
  selectionType: types.enumeration<SELECTION_TYPE>(
    'SelectionType',
    Object.values(SELECTION_TYPE),
  ),
  simplifiedAnswers: types.maybeNull(types.array(QuestionnaireAnswer)),
  controlType: types.maybeNull(
    types.enumeration<CONTROL_TYPE>(
      'ControlTypes',
      Object.values(CONTROL_TYPE),
    ),
  ),
  mandatory: types.maybeNull(
    types.model({ mandatoryAnswerText: types.array(types.string) }),
  ),
});

export const QuestionnaireQuestion = types
  .model({
    id: types.maybeNull(types.string),
    globalId: types.maybeNull(types.string),
    text: types.string,
    statement: types.maybeNull(types.string),
    essential: types.maybeNull(types.boolean),
    source: types.enumeration<RR_QUESTION_SOURCE>(
      'Source',
      Object.values(RR_QUESTION_SOURCE),
    ),
    answerType: types.enumeration<ANSWER_TYPE>(
      'AnswerType',
      Object.values(ANSWER_TYPE),
    ),
    answers: types.maybeNull(types.array(QuestionnaireAnswer)),
    order: types.number,
    hirerExperience: types.maybeNull(HirerExperience),
  })
  .views((self) => ({
    getHirerControlType() {
      if (self?.hirerExperience?.controlType) {
        return self.hirerExperience.controlType;
      }
      if (
        self.id &&
        ['7', '8', '161'].includes(self.id) &&
        isAuOrNzRRQuestion(self.globalId) // TODO: pending if RR sends us HirerControlType
      ) {
        return 'Range';
      }
      return null;
    },
    get globalIdOrId() {
      return self.globalId ?? (self.id as string);
    },
  }));

export interface IQuestionnaireQuestion
  extends Instance<typeof QuestionnaireQuestion> {}

const QuestionnaireStore = types
  .model({
    id: types.optional(types.string, ''),
    questions: types.maybeNull(types.array(QuestionnaireQuestion)),
    state: stateType,
  })
  .views((self) => ({
    get rootStore(): IStore {
      return getRoot(self);
    },
  }))
  .views((self) => ({
    get isError() {
      return self.state === STATE_ERROR;
    },
    get hasQuestion() {
      return Boolean(self.questions && self.questions.length > 0);
    },
    getSimplifiedAnswer({
      questionId,
      questionGlobalId,
      answerId,
      answerGlobalId,
    }: {
      questionId: string | null;
      questionGlobalId: string | null;
      answerId: string | null;
      answerGlobalId: string | null;
    }): string | undefined {
      const matchingQuestion = self.questions?.find(
        (question) =>
          question.id === questionId || question.globalId === questionGlobalId,
      );
      const hirerExperience = matchingQuestion?.hirerExperience;

      // check scenario for null hardcoded hirerExperience
      if (!hirerExperience && questionId && answerId) {
        return getHardcodedSimplifiedAnswerText({
          questionId,
          questionGlobalId,
          answerId,
        });
      }

      if (!hirerExperience) {
        return;
      }

      if (hirerExperience.selectionType === SELECTION_TYPE.ANSWERS) {
        return;
      }
      if (hirerExperience.selectionType === SELECTION_TYPE.SIMPLIFIED_ANSWERS) {
        const simplifiedAnswer = hirerExperience?.simplifiedAnswers?.find(
          (answer) =>
            answer?.answerGlobalIdsOrIds?.some(
              (hardcodedAnswerId) =>
                hardcodedAnswerId === answerId ||
                hardcodedAnswerId === answerGlobalId,
            ),
        );
        return simplifiedAnswer?.text;
      }

      // track warning or something here?
      return;
    },
    getMandatoryQuestions(): MandatoryQuestions {
      if (self.state === 'error') {
        return {
          success: false,
        };
      }

      return {
        success: true,
        data:
          self.questions?.reduce<
            Array<{ question: string; answers: string[] }>
          >((mandatoryQuestions, question) => {
            if (question?.hirerExperience?.mandatory) {
              mandatoryQuestions.push({
                question: question.statement || question.text,
                answers: question.hirerExperience.mandatory.mandatoryAnswerText,
              });
            }

            return mandatoryQuestions;
          }, []) ?? [],
      };
    },
  }))
  .actions((self) => {
    const loadQuestionnaire = flow(function* (adcentreAdvertiserId: string) {
      const questionnaireId = self.rootStore.job.jobInformation.questionnaireId;
      if (!questionnaireId) {
        self.state = STATE_LOADED;
        return;
      }
      try {
        self.id = questionnaireId;
        self.questions = null;

        const questionnaire: any = yield fetchQuestionnaire(
          questionnaireId,
          adcentreAdvertiserId,
        );
        self.state = STATE_LOADED;

        if (questionnaire) {
          self.questions = questionnaire.questions;
        }
        if (self.questions) {
          self.questions.forEach((question) => {
            // Remove the following condition after <date> (TBD)
            // Temporarily hard coding simplified answers for jbs created prior
            // to the release new hirerExperience field in RR-API
            if (!question.hirerExperience && question.id) {
              const preferredAnswersIds = question.answers
                ?.filter((answer) => answer.isPreferred)
                ?.map((answer) => answer.id)
                ?.filter((answerId): answerId is string => answerId !== null);
              const simplifiedAnswers = buildSimplifiedAnswers({
                questionId: question.id,
                questionGlobalId: question.globalId,
                preferredAnswersIds: preferredAnswersIds ?? [],
              });
              if (simplifiedAnswers) {
                const hirerExperience = {
                  selectionType: SELECTION_TYPE.SIMPLIFIED_ANSWERS,
                  simplifiedAnswers,
                };
                question.hirerExperience = cast(hirerExperience);
              }
            }
          });
        }
      } catch (error: any) {
        logError(
          {
            errorCode: 'GET_QUESTIONNAIRE',
            description: 'Error attempting: fetchQuestionnaire',
          },
          { error, questionnaireId, adcentreAdvertiserId },
        );
        self.state = STATE_ERROR;
      }
    });
    return { loadQuestionnaire };
  });
export interface IQuestionnaireStore
  extends Instance<typeof QuestionnaireStore> {}
export interface ISimplifiedAnswerIds
  extends Instance<typeof simplifiedAnswerIds> {}
export default QuestionnaireStore;
